"use client"

import type React from "react"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import Link from "next/link"
import { RequestDemoButton } from "@/components/request-demo-button"

export default function SignupPage() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [company_name, setCompanyName] = useState("")
  const [phone, setPhone] = useState("")
  const [business_type, setBusinessType] = useState("shop")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password, company_name, phone, business_type }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || "Signup failed")
      }

      const encodedEmail = encodeURIComponent(email)
      router.push(`/check-email?email=${encodedEmail}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=2074')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-blue-900/85 to-gray-900/90 backdrop-blur-sm"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:block text-white space-y-6">
          <div className="flex items-center mb-8">
            <img src="/envotech.png" alt="EnvoTech" className="h-14 w-auto object-contain brightness-0 invert" />
          </div>
          
          <h1 className="text-5xl font-bold leading-tight">
            Start Growing
            <br />
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              Your Business Today
            </span>
          </h1>
          
          <p className="text-xl text-blue-100 leading-relaxed">
            Join thousands of businesses that trust EnvoTech CRM to manage their customers, track orders, and increase revenue.
          </p>

          <div className="space-y-4 pt-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">First month free with full Pro features</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">Set up in under 5 minutes</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">24/7 customer support included</span>
            </div>
            <div className="pt-4">
              <RequestDemoButton size="default" className="bg-white text-blue-900 border-white hover:bg-white/90 hover:text-blue-900 font-semibold" />
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="w-full">
          <Card className="w-full max-w-md mx-auto shadow-2xl border-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md">
            <CardHeader className="space-y-2 text-center pb-6">
              <div className="lg:hidden flex justify-center mb-4">
                <img src="/envotech.png" alt="EnvoTech" className="h-12 w-auto object-contain" />
              </div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-green-700 to-blue-800 bg-clip-text text-transparent">
                Get Started Free
              </CardTitle>
              <CardDescription className="text-base">
                Create your business account in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-200">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {/* Form Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Business Name - Full Width */}
                  <div className="col-span-2 space-y-2">
                    <label htmlFor="company_name" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Business Name
                    </label>
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="Your Business Name"
                      value={company_name}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>

                  {/* Business Type - Full Width */}
                  <div className="col-span-2 space-y-2">
                    <label htmlFor="business_type" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Business Type
                    </label>
                    <select
                      id="business_type"
                      value={business_type}
                      onChange={(e) => setBusinessType(e.target.value)}
                      className="w-full h-11 rounded-md border-2 border-input bg-background px-4 text-sm ring-offset-background focus-visible:outline-none focus-visible:border-blue-700 transition-colors"
                    >
                      <option value="shop">Shop</option>
                      <option value="tuition">Tuition</option>
                      <option value="salon">Salon</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Full Name - Left Column */}
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Full Name
                    </label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>

                  {/* Phone - Right Column */}
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+94 77 123 4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>

                  {/* Email - Full Width */}
                  <div className="col-span-2 space-y-2">
                    <label htmlFor="email" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>

                  {/* Password - Left Column */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>

                  {/* Confirm Password - Right Column */}
                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Confirm Password
                    </label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-11 px-4 border-2 focus:border-blue-700 transition-colors"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-800 to-green-700 hover:from-blue-900 hover:to-green-800 shadow-lg hover:shadow-xl transition-all mt-6"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Creating account...</span>
                    </div>
                  ) : (
                    "Create My Account"
                  )}
                </Button>

                <p className="text-xs text-center text-gray-500 dark:text-gray-400 pt-2">
                  By signing up, you agree to our Terms of Service and Privacy Policy
                </p>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white dark:bg-gray-900 text-gray-500">Already have an account?</span>
                  </div>
                </div>

                <div className="text-center">
                  <Link href="/login">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full h-11 text-base font-semibold border-2 border-gray-300 dark:border-gray-600 hover:bg-green-50 hover:border-green-300 hover:text-green-700 dark:hover:bg-green-950 dark:hover:border-green-700 dark:hover:text-green-300 transition-colors"
                    >
                      Sign In Instead
                    </Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-white/80 mt-6 lg:hidden">
            🎉 First month free with full Pro features
          </p>
        </div>
      </div>
    </div>
  )
}
